import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDownward as DownIcon,
  ArrowUpward as UpIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

/**
 * Eskalationsanrufe (KI-DISPONENT_KONZEPT.md §4).
 *
 * Bei kritischen Ereignissen ruft der KI-Assistent zusätzlich zu Glocke und Web-Push die
 * hinterlegten Portal-Zugänge der Reihe nach an, bis jemand übernimmt. Einstellungen liegen
 * nicht öffentlich und laufen über die Callable manageAiEscalationSettings (nur
 * Administratoren der Zentrale). Angerufen werden Mitarbeiter mit Portal-Zugang (aus manageAiCallerPin,
 * nur mit Nummer) oder freie Nummern mit Bezeichnung, z. B. ein Bereitschaftshandy.
 */
const callSettings = (payload) => httpsCallable(functions, 'manageAiEscalationSettings')(payload);
const callPin = (payload) => httpsCallable(functions, 'manageAiCallerPin')(payload);

const EVENT_LABELS = {
  portal_dispatch_no_candidates: 'Fahrt nicht vermittelbar (kein passendes Fahrzeug)',
  portal_dispatch_failed: 'Disposition fehlgeschlagen',
  portal_preorder_unassigned: 'Vorbestellung kurz vor Abholung noch ohne Fahrer',
  portal_preorder_not_started: 'Vorbestellung nicht angetreten',
  portal_preorder_capability_risk: 'Spezial-Vorbestellung ohne passendes Fahrzeug im Plan',
  portal_assignment_rejected: 'Fahrer hat Zuweisung abgelehnt',
  portal_ride_auto_cancelled: 'Fahrt automatisch storniert (kein Fahrer gefunden)'
};

// Eintrag „+ Mitarbeiter anlegen": öffnet Name + Nummer (nur Anrufliste, kein Portal-Zugang).
const NEW_NUMBER = '__new_number__';

const STATUS_CHIPS = {
  pending: { label: 'Wartet', color: 'default' },
  calling: { label: 'Ruft an', color: 'info' },
  acknowledged: { label: 'Übernommen', color: 'success' },
  exhausted: { label: 'Niemand übernommen', color: 'warning' },
  cancelled: { label: 'Erledigt', color: 'default' },
  failed: { label: 'Fehler', color: 'error' }
};

const formatTime = (ms) => (ms
  ? new Date(ms).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '');

const AiEscalationSettingsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [settings, setSettings] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [outboundConfigured, setOutboundConfigured] = useState(true);
  const [recent, setRecent] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [addUserId, setAddUserId] = useState('');
  const [addLabel, setAddLabel] = useState('');
  const [addPhone, setAddPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await callSettings({ op: 'get' });
      setSettings(res.data.settings);
      setEventTypes(res.data.eventTypes || []);
      setOutboundConfigured(res.data.outboundConfigured !== false);
      setRecent(res.data.recent || []);
      setAllowed(true);
      try {
        const list = await callPin({ op: 'list' });
        setAccounts(list.data?.accounts || []);
      } catch (_) {
        setAccounts([]);
      }
    } catch (_) {
      // Kein Administrator der Zentrale: Bereich ausblenden.
      setAllowed(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const accountById = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);
  const selectable = accounts.filter((a) => a.phones.length > 0 &&
    !(settings?.targets || []).some((t) => t.userId === a.id));

  if (loading || !allowed || !settings) {
    return loading ? (
      <Box sx={{ px: 3, py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
    ) : null;
  }

  const update = (patch) => setSettings((prev) => ({ ...prev, ...patch }));
  const updateTarget = (index, patch) => update({
    targets: settings.targets.map((t, i) => (i === index ? { ...t, ...patch } : t))
  });
  const moveTarget = (index, delta) => {
    const next = [...settings.targets];
    const [item] = next.splice(index, 1);
    next.splice(index + delta, 0, item);
    update({ targets: next });
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const res = await callSettings({ op: 'set', settings });
      setSettings(res.data.settings);
      setFeedback({ severity: 'success', message: 'Eskalationsanrufe gespeichert.' });
    } catch (error) {
      setFeedback({ severity: 'error', message: error?.message || 'Speichern fehlgeschlagen.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">Eskalationsanrufe</Typography>
        <Switch
          checked={settings.enabled}
          onChange={(e) => update({ enabled: e.target.checked })}
          color="primary"
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
        Bei kritischen Ereignissen ruft der KI-Assistent zusätzlich zur Meldung im Portal die
        folgenden Mitarbeiter der Reihe nach an, bis jemand die Meldung übernimmt. Angerufen wird nur
        innerhalb des jeweiligen Zeitfensters. Löst sich das Problem vorher von selbst, entfällt der Anruf.
      </Typography>

      {!outboundConfigured && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Ausgehende Anrufe sind für dieses Konto noch nicht eingerichtet. Bitte wenden Sie sich an NextCab.
        </Alert>
      )}

      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Wer wird angerufen?</Typography>
      {settings.targets.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Noch kein Mitarbeiter hinterlegt.
        </Typography>
      )}
      <List dense disablePadding>
        {settings.targets.map((target, index) => {
          const account = target.userId ? accountById.get(target.userId) : null;
          const name = target.userId ? (account?.name || 'Unbekannter Zugang') : (target.label || 'Bereitschaftsnummer');
          const secondary = target.userId
            ? (account?.phones?.length ? account.phones.join(' · ') : 'Keine Telefonnummer hinterlegt — wird übersprungen')
            : target.phone;
          return (
            <ListItem key={target.userId || target.phone} disableGutters sx={{ flexWrap: 'wrap', gap: 1 }}>
              <ListItemText
                sx={{ minWidth: 180, flex: '1 1 180px' }}
                primary={`${index + 1}. ${name}`}
                secondary={secondary}
              />
              <TextField
                size="small" type="time" label="von" value={target.from}
                onChange={(e) => updateTarget(index, { from: e.target.value })}
                InputLabelProps={{ shrink: true }} sx={{ width: 110 }}
              />
              <TextField
                size="small" type="time" label="bis" value={target.to === '24:00' ? '23:59' : target.to}
                onChange={(e) => updateTarget(index, { to: e.target.value === '23:59' ? '24:00' : e.target.value })}
                InputLabelProps={{ shrink: true }} sx={{ width: 110 }}
              />
              <IconButton size="small" disabled={index === 0} onClick={() => moveTarget(index, -1)}><UpIcon fontSize="inherit" /></IconButton>
              <IconButton size="small" disabled={index === settings.targets.length - 1} onClick={() => moveTarget(index, 1)}><DownIcon fontSize="inherit" /></IconButton>
              <IconButton size="small" onClick={() => update({ targets: settings.targets.filter((_, i) => i !== index) })}><DeleteIcon fontSize="inherit" /></IconButton>
            </ListItem>
          );
        })}
      </List>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }} sx={{ mt: 1, mb: 2 }}>
        <TextField
          select size="small" label="Mitarbeiter hinzufügen" value={addUserId}
          onChange={(e) => setAddUserId(e.target.value)} sx={{ minWidth: 240 }}
          helperText={addUserId === NEW_NUMBER ? 'Nur für diese Anrufliste, ohne Portal-Zugang. Die Nummer wird beim Speichern geprüft.' : 'Mitarbeiter mit Portal-Zugang erscheinen nur mit hinterlegter Telefonnummer'}
        >
          {selectable.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
          <MenuItem value={NEW_NUMBER}>
            <AddIcon fontSize="small" sx={{ mr: 1 }} />
            Mitarbeiter anlegen
          </MenuItem>
        </TextField>
        {addUserId === NEW_NUMBER && (
          <>
            <TextField
              size="small" label="Name" placeholder="z. B. Bereitschaftshandy"
              value={addLabel} onChange={(e) => setAddLabel(e.target.value)} sx={{ minWidth: 200 }}
            />
            <TextField
              size="small" label="Telefonnummer" placeholder="z. B. 0176 12345678"
              value={addPhone} onChange={(e) => setAddPhone(e.target.value)} sx={{ minWidth: 200 }}
              autoFocus
            />
          </>
        )}
        <Button
          size="small" variant="outlined"
          disabled={!addUserId || (addUserId === NEW_NUMBER && !addPhone.trim())}
          onClick={() => {
            const target = addUserId === NEW_NUMBER
              ? { phone: addPhone.trim(), label: addLabel.trim() }
              : { userId: addUserId };
            update({ targets: [...settings.targets, { ...target, from: '00:00', to: '24:00' }] });
            setAddUserId('');
            setAddPhone('');
            setAddLabel('');
          }}
          sx={{ mt: { sm: 0.5 } }}
        >
          Hinzufügen
        </Button>
      </Stack>

      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Bei welchen Ereignissen?</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
        {eventTypes.map((type) => (
          <FormControlLabel
            key={type}
            control={(
              <Checkbox
                size="small"
                checked={settings.events[type] === true}
                onChange={(e) => update({ events: { ...settings.events, [type]: e.target.checked } })}
              />
            )}
            label={<Typography variant="body2">{EVENT_LABELS[type] || type}</Typography>}
          />
        ))}
      </Box>

      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Ablauf</Typography>
      <Stack spacing={2} sx={{ mb: 2, maxWidth: 520 }}>
        <TextField
          select size="small" fullWidth label="Wartezeit bis zum nächsten Mitarbeiter" value={settings.retryMinutes}
          onChange={(e) => update({ retryMinutes: Number(e.target.value) })}
          helperText="So lange wartet die KI auf eine Zusage. Nimmt niemand ab oder sagt jemand ab, ruft sie danach den nächsten Mitarbeiter der Liste an."
        >
          {[2, 3, 5, 10].map((m) => <MenuItem key={m} value={m}>{m} Minuten</MenuItem>)}
        </TextField>
        <TextField
          select size="small" fullWidth label="Wenn gerade kein Mitarbeiter im Zeitfenster ist" value={settings.outsideWindows}
          onChange={(e) => update({ outsideWindows: e.target.value })}
          helperText="Zum Beispiel nachts, wenn alle Zeitfenster tagsüber liegen."
        >
          <MenuItem value="none">Niemanden anrufen – nur Meldung im Portal</MenuItem>
          <MenuItem value="last_target">Den letzten Mitarbeiter der Liste trotzdem anrufen</MenuItem>
        </TextField>
        <TextField
          select size="small" fullWidth label="Höchstzahl Anrufe pro Stunde" value={settings.maxCallsPerHour}
          onChange={(e) => update({ maxCallsPerHour: Number(e.target.value) })}
          helperText="Schutz vor Anrufserien, wenn viele Meldungen gleichzeitig auftreten. Darüber hinaus gibt es nur die Meldung im Portal."
        >
          {[3, 6, 10, 20].map((n) => <MenuItem key={n} value={n}>{n} Anrufe</MenuItem>)}
        </TextField>
      </Stack>

      <Button variant="contained" size="small" onClick={handleSave} disabled={saving}>
        {saving ? 'Wird gespeichert…' : 'Speichern'}
      </Button>
      {feedback && (
        <Alert severity={feedback.severity} sx={{ mt: 1.5 }} onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      {recent.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Letzte Eskalationen</Typography>
          <List dense disablePadding>
            {recent.map((e) => {
              const chip = STATUS_CHIPS[e.status] || { label: e.status, color: 'default' };
              return (
                <ListItem key={e.id} disableGutters>
                  <ListItemText
                    primary={(
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <span>{EVENT_LABELS[e.eventType] || e.title}</span>
                        <Chip size="small" color={chip.color} label={chip.label} />
                      </Stack>
                    )}
                    secondary={[
                      formatTime(e.createdAt),
                      e.attempts ? `${e.attempts} Anruf${e.attempts === 1 ? '' : 'e'}` : null,
                      e.acknowledgedBy ? `übernommen von ${e.acknowledgedBy}` : null
                    ].filter(Boolean).join(' · ')}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default AiEscalationSettingsPanel;
